import React from 'react'
import {observer} from 'mobx-react-lite'
import {useProductStore} from '../../store/hooks/useStore'
import {usePromotion} from '../../store/hooks/usePromotion'
import {Box, BoxProps, Flex, Stack, Text} from '../../vanilla'
import {Link} from '../link'
import {Price} from '../price'
import {PromoPills} from '../promo-pill'
import {ChevronRightIcon} from '../icons'
import {
  cartItemActions,
  cartItemDetails,
  cartItemDrawerDetails,
  cartItemImage,
  cartItemPrice,
  cartItemQuantity
} from './styles.css'
import classNames from 'classnames'
import {ITEM_LOCATION} from '../../analytics/utils'
import {MealDealGroup} from '../../types/store/basket'
import MealDealQuantityInput from '../../pages/meal-deals/meal-deal-quantity-input'
import MealDealRemove from '../../pages/meal-deals/meal-deal-remove-button'
import {calculateItemPrice} from '../../components/algolia/util/calculateItemPrice'
import {useGlobalStore, useRapidDeliveryStore, useCustomerStore} from '../../store/hooks/useStore'

interface MealDealCartItemProps extends BoxProps<'div'> {
  id: string
  className?: string
  inDrawer?: boolean
  group: MealDealGroup
}

const MealDealCartItemContainer = ({
  id,
  className,
  inDrawer,
  group,
  ...props
}: MealDealCartItemProps) => {
  const productStore = useProductStore()
  const promotion = usePromotion(group?.promoID)
  const {selectedPriceDayOffset, selectedRegion} = useGlobalStore()
  const {isRapidPricingActive} = useRapidDeliveryStore()
  const {getApplicablePromotions} = useCustomerStore()

  if (!promotion) return null

  const promoLink = `/meal-deals?promo=${group?.promoID}`
  const promoPrice = Number(promotion.price || 0)
  const promoDisplayPrice = `£${promoPrice.toFixed(2)}`

  // Calculate savings for the group of items in the meal deal compared to list price.

  const mealDealProducts = group?.items ?? []
  const actualCost = mealDealProducts.reduce((total, mealDealProduct) => {
    const product = productStore.productsById[mealDealProduct.productID]
    const productPrice = product?.price?.GBP ?? 0
    const {displayPrice} = calculateItemPrice({
      hit: product,
      selectedPriceDayOffset,
      selectedRegion,
      isRapidPricingActive,
      getApplicablePromotions
    })
    return total + (displayPrice || productPrice) * mealDealProduct.displayQuantity * group.quantity
  }, 0)
  const actualSavings = actualCost - promoPrice * group.quantity

  // Set up optional promotion pills based on actual savings and serving size
  const {servingSize} = promotion.c_promotionPills ?? {}
  const pills = {
    ...(actualSavings >= 0.5 ? {actualSavings} : {}),
    servingSize
  }

  const getCartLocation = () => {
    if (location.pathname === '/basket') {
      return ITEM_LOCATION.BASKET
    }
    if (location.pathname === '/checkout/summary') {
      return ITEM_LOCATION.CHECKOUT
    }
    return ITEM_LOCATION.MINIBASKET
  }

  const locationOfCart = getCartLocation()
  const isMiniBasket = locationOfCart === ITEM_LOCATION.MINIBASKET

  return (
    <Flex
      position="relative"
      padding="16px"
      flexWrap="wrap"
      justifyContent="flex-end"
      alignItems="center"
      borderBottom="1px"
      borderColor="gray200"
      className={classNames(className)}
      {...props}
    >
      <Box
        as={Link}
        href={promoLink}
        ratio="1"
        width="full"
        flexShrink="0"
        position="relative"
        className={cartItemImage}
        marginBottom={'16px'}
        paddingRight={isMiniBasket ? '16px' : '0px'}
      >
        <Box
          background="mealDealRed"
          width="full"
          textTransform="capitalize"
          alignContent="center"
          justifyContent="center"
          textAlign="center"
          color="white"
          borderRadius="medium"
          ratio="1"
        >
          <Text fontSize={['md', 'lg']} lineHeight={['short', 'taller']} textAlign="center">
            Meal Deal
          </Text>
        </Box>
      </Box>
      <Box className={inDrawer ? cartItemDrawerDetails : cartItemDetails} alignSelf="flex-start">
        <Stack>
          <Flex
            as={Link}
            href={promoLink}
            justifyContent="space-between"
            alignItems="center"
            background="offerYellow"
            borderRadius="base"
            paddingY={'6px'}
            paddingX={'8px'}
          >
            <Text variant="text5">{promotion?.calloutMsg || promotion?.name}</Text>
            <ChevronRightIcon
              height="14px"
              width="14px"
              marginLeft="auto"
              marginRight="0px"
              alignSelf="center"
            />
          </Flex>
          <PromoPills
            pills={pills}
            styling={{marginBottom: '0px', background: '#FFF'}}
          />
          {group?.items.map((item, idx) => (
            <Text>
              {item.displayQuantity} x {productStore.productsById[item.productID]?.name}
            </Text>
          ))}
        </Stack>
      </Box>
      <Box className={cartItemQuantity}>
        <Box paddingRight={'16px'}>
          <MealDealQuantityInput
            cartLocation={locationOfCart}
            group={group}
            inCart={true}
            size="sm"
            variant="outlineDark"
          />
        </Box>
      </Box>
      <Box className={cartItemPrice} paddingLeft={'0px'} marginLeft={'auto'} marginRight={'auto'}>
        <Price
          promotionPrice={promoPrice}
          price={parseInt(promotion?.price || '')}
          quantity={group?.quantity}
          priceInfo={{perUnit: '', wasPrice: ''}}
          isBasket={true}
          dataTestSelector={inDrawer ? 'cart-list-item-price' : undefined}
        />
        <Text display={'block'} textAlign={'right'} marginRight={'24px'} variant="text0">
          {promoDisplayPrice} per deal
        </Text>
      </Box>
      <Box className={cartItemActions}>
        <MealDealRemove
          cartLocation={locationOfCart}
          group={group}
          inCart={true}
        />
      </Box>
    </Flex>
  )
}

export const MealDealCartItem = observer(MealDealCartItemContainer)
